import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, fmtMoney, fmtDate } from '../lib/api.js';

const STATUSES = ['pending', 'approved', 'active', 'denied', 'closed'];

export default function USDA() {
  const [list, setList] = useState(null);
  const [err, setErr] = useState('');
  const [showForm, setShowForm] = useState(false);

  async function load() {
    try { const d = await api.get('/api/usda'); setList(d.programs); } catch (e) { setErr(e.message); }
  }
  useEffect(() => { load(); }, []);

  if (err) return <div className="text-rust-700 text-sm">{err}</div>;
  if (!list) return <div className="text-ink-700/60 italic">Loading…</div>;

  const totals = list.reduce((acc, p) => {
    if (p.status === 'pending') acc.pending += p.expected_amount_cents || 0;
    if (p.status === 'approved' || p.status === 'active') acc.approved += p.expected_amount_cents || 0;
    acc.received += p.received_amount_cents || 0;
    return acc;
  }, { pending: 0, approved: 0, received: 0 });

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-serif text-olive-900">USDA Programs</h1>
        <button onClick={() => setShowForm(s => !s)} className="btn-primary">
          {showForm ? 'Cancel' : '+ Add'}
        </button>
      </header>

      <div className="grid grid-cols-3 gap-3">
        <SummaryCard label="Pending applications" value={fmtMoney(totals.pending)} tone="gold" />
        <SummaryCard label="Approved (expected)" value={fmtMoney(totals.approved)} tone="olive" />
        <SummaryCard label="Received YTD" value={fmtMoney(totals.received)} tone="olive" />
      </div>

      {showForm && <USDAForm onDone={() => { setShowForm(false); load(); }} />}

      {list.length === 0 ? (
        <div className="card text-center py-10 text-ink-700/70">No programs yet.</div>
      ) : (
        <ul className="space-y-2">
          {list.map(p => (
            <li key={p.id} className="card">
              <div className="flex items-start gap-3">
                <span className={`pill ${pillCls(p.status)}`}>{p.status}</span>
                <div className="flex-1">
                  <div className="font-medium text-ink-900">{p.program_name}</div>
                  <div className="text-xs text-ink-700/60 mt-1 flex flex-wrap gap-x-3">
                    {p.application_date && <span>Applied {fmtDate(p.application_date)}</span>}
                    {p.related_project_name && <span>Project: <Link to={`/projects/${p.related_project_id}`} className="text-olive-700 hover:underline">{p.related_project_name}</Link></span>}
                    {p.agency_contact && <span>{p.agency_contact}</span>}
                  </div>
                  {p.notes && <div className="text-sm text-ink-700/80 mt-1 italic">{p.notes}</div>}
                </div>
                <div className="text-right text-sm flex flex-col items-end gap-1">
                  {p.expected_amount_cents != null && <div>Expected: <strong>{fmtMoney(p.expected_amount_cents)}</strong></div>}
                  {p.received_amount_cents > 0 && <div className="text-olive-700">Received: <strong>{fmtMoney(p.received_amount_cents)}</strong></div>}
                  <div className="flex gap-2 text-xs mt-1">
                    {p.status !== 'closed' && (
                      <button onClick={async () => {
                        try { await api.patch(`/api/usda/${p.id}`, { status: 'closed' }); load(); }
                        catch (e) { setErr(e.message); }
                      }} className="btn-ghost text-xs py-0.5 px-2">Close</button>
                    )}
                    {p.status === 'closed' && (
                      <button onClick={async () => {
                        try { await api.patch(`/api/usda/${p.id}`, { status: 'pending' }); load(); }
                        catch (e) { setErr(e.message); }
                      }} className="btn-ghost text-xs py-0.5 px-2">Reopen</button>
                    )}
                    <button onClick={async () => {
                      if (!confirm(
                        `Delete "${p.program_name}"?\n\n` +
                        `Income / expense rows tagged to it will be un-tagged ` +
                        `(they survive at segment level). Marking it closed is usually enough.`,
                      )) return;
                      try {
                        const r = await api.delete(`/api/usda/${p.id}`);
                        const u = r.untagged || {};
                        alert(`✓ Deleted. Un-tagged ${u.income || 0} income, ${u.expense_lines || 0} expense lines.`);
                        load();
                      } catch (e) { setErr(e.message); }
                    }} className="text-xs text-rust-700 hover:underline px-1">Delete</button>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function pillCls(status) {
  return {
    pending:  'bg-gold-500/15 text-gold-600',
    approved: 'bg-olive-100 text-olive-900',
    active:   'bg-olive-100 text-olive-900',
    denied:   'bg-rust-500/15 text-rust-700',
    closed:   'bg-ink-700/10 text-ink-700',
  }[status] || 'bg-ink-700/10 text-ink-700';
}

function SummaryCard({ label, value, tone }) {
  const c = tone === 'gold' ? 'text-gold-600' : tone === 'olive' ? 'text-olive-900' : 'text-ink-900';
  return (
    <div className="card text-center">
      <div className="text-xs uppercase tracking-wider text-ink-700/60">{label}</div>
      <div className={`text-xl font-semibold mt-1 ${c}`}>{value}</div>
    </div>
  );
}

function USDAForm({ onDone }) {
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({
    program_name: '', agency_contact: '',
    application_date: new Date().toISOString().slice(0, 10),
    status: 'pending',
    expected_dollars: '', received_dollars: '',
    related_project_id: '', notes: '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get('/api/projects').then(({ projects }) => setProjects(projects)).catch(e => setErr(e.message));
  }, []);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const body = {
        program_name: form.program_name.trim(),
        agency_contact: form.agency_contact.trim() || null,
        application_date: form.application_date || null,
        status: form.status,
        expected_amount_cents: form.expected_dollars ? Math.round(Number(form.expected_dollars) * 100) : null,
        received_amount_cents: form.received_dollars ? Math.round(Number(form.received_dollars) * 100) : 0,
        related_project_id: form.related_project_id ? Number(form.related_project_id) : null,
        notes: form.notes.trim() || null,
      };
      await api.post('/api/usda', body);
      onDone();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  return (
    <form onSubmit={submit} className="card grid grid-cols-2 gap-3">
      <div className="col-span-2">
        <label className="label">Program name *</label>
        <input className="input" required value={form.program_name} onChange={e => set('program_name', e.target.value)} placeholder="EQIP irrigation cost-share" />
      </div>
      <div>
        <label className="label">Status</label>
        <select className="input" value={form.status} onChange={e => set('status', e.target.value)}>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div>
        <label className="label">Application date</label>
        <input className="input" type="date" value={form.application_date} onChange={e => set('application_date', e.target.value)} />
      </div>
      <div>
        <label className="label">Expected $</label>
        <input className="input" type="number" step="0.01" value={form.expected_dollars} onChange={e => set('expected_dollars', e.target.value)} />
      </div>
      <div>
        <label className="label">Received $ (so far)</label>
        <input className="input" type="number" step="0.01" value={form.received_dollars} onChange={e => set('received_dollars', e.target.value)} />
      </div>
      <div className="col-span-2">
        <label className="label">Linked project</label>
        <select className="input" value={form.related_project_id} onChange={e => set('related_project_id', e.target.value)}>
          <option value="">— none —</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>
      <div className="col-span-2">
        <label className="label">Agency contact</label>
        <input className="input" value={form.agency_contact} onChange={e => set('agency_contact', e.target.value)} placeholder="USDA Tate County NRCS" />
      </div>
      <div className="col-span-2">
        <label className="label">Notes</label>
        <textarea className="input" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
      </div>
      {err && <div className="col-span-2 bg-rust-500/10 text-rust-700 rounded-md px-3 py-2 text-sm">{err}</div>}
      <div className="col-span-2 flex justify-end">
        <button className="btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Add program'}</button>
      </div>
    </form>
  );
}
